import { useEffect, useRef, useState } from "react";
import Sentry from "@/lib/sentry-safe";
import {
  EnvelopeFollower,
  normalizeRmsLevel,
} from "@/lib/audio-envelope";

/**
 * A given <audio> element can have at most one MediaElementAudioSourceNode
 * created from it, ever (browser-enforced, throws InvalidStateError on a
 * second attempt) — so a per-element cache guards against re-creating one if
 * this hook's effect ever re-runs against the same element (e.g. a
 * remount while playback continues). Every new sound gets a brand-new
 * <audio> element (hooks/useAudioPlayer.ts), so in practice this cache
 * rarely holds more than the current element, but it makes reuse safe
 * rather than merely untested.
 */
const sourceNodeCache = new WeakMap<
  HTMLAudioElement,
  { source: MediaElementAudioSourceNode; analyser: AnalyserNode }
>();

/**
 * Live 0..1 signal level for a playing <audio> element, run through the
 * §11.3 envelope follower + flash limiter. Returns 0 when not playing or
 * when Web Audio is unavailable (analysis is a decoration, never a
 * requirement — playback must never depend on it).
 *
 * `prefersReducedMotion` freezes the value at the current RMS envelope
 * instead of repainting every frame, per §03.1's reduced-motion note.
 */
export function useAudioLevel(
  audioEl: HTMLAudioElement | null,
  isPlaying: boolean,
  prefersReducedMotion = false
): number {
  const [level, setLevel] = useState(0);
  const followerRef = useRef<EnvelopeFollower | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  // The <audio> element the current sourceRef/analyserRef were built for.
  // A new sound gets a brand-new element (hooks/useAudioPlayer.ts), so this
  // lets setup() tell "same element, effect re-ran" (reuse) apart from
  // "different element, sound changed" (rebuild) instead of only checking
  // whether a source already exists at all.
  const sourceElRef = useRef<HTMLAudioElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef<number>(0);

  useEffect(() => {
    if (!followerRef.current) followerRef.current = new EnvelopeFollower();
  }, []);

  useEffect(() => {
    if (!audioEl || !isPlaying) {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    let cancelled = false;

    const setup = () => {
      try {
        if (!ctxRef.current) {
          const Ctor =
            window.AudioContext ||
            (window as unknown as { webkitAudioContext?: typeof AudioContext })
              .webkitAudioContext;
          if (!Ctor) return false;
          ctxRef.current = new Ctor();
        }
        const ctx = ctxRef.current;
        // `resume()` is a promise, and the surrounding try/catch only sees
        // synchronous throws — `void` discarded the rejection straight into
        // `window.onunhandledrejection`, which is how Safari's
        // `InvalidStateError: Failed to start the audio device` reached Sentry
        // with no first-party frames (TESLA-LOCK-SOUND-6V). That error is the
        // visitor's output device, not a defect in the catalog: it recurs on
        // every play on an affected machine, so reporting it bills in
        // proportion to traffic rather than to distinct problems. Swallow it
        // for the same reason the catch below is non-fatal — analysis is a
        // decoration, and the transport reads from `timeupdate` regardless.
        if (ctx.state === "suspended") void ctx.resume().catch(() => {});

        if (sourceElRef.current !== audioEl) {
          // The element changed (a new sound started — hooks/useAudioPlayer.ts
          // creates a fresh <audio> per src) or this is the first run. Either
          // way the existing source/analyser (if any) belong to a different,
          // now-irrelevant element and must be disconnected before building
          // new ones — leaving them wired up would silently keep reading the
          // old (paused, silent) element's level forever.
          if (sourceRef.current) {
            try {
              sourceRef.current.disconnect();
              analyserRef.current?.disconnect();
            } catch {
              // best-effort — a node from a closed context throws harmlessly
            }
          }

          const cached = sourceNodeCache.get(audioEl);
          if (cached) {
            // This exact element already has a MediaElementAudioSourceNode
            // (e.g. the hook's effect re-ran against an unchanged element) —
            // reuse it. Calling createMediaElementSource on it again would
            // throw InvalidStateError (one source node per element, ever).
            sourceRef.current = cached.source;
            analyserRef.current = cached.analyser;
          } else {
            const source = ctx.createMediaElementSource(audioEl);
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 256;
            analyser.smoothingTimeConstant = 0;
            source.connect(analyser);
            // Routing the element through a MediaElementAudioSourceNode
            // replaces its default output — it goes silent unless the graph
            // is explicitly reconnected to the destination.
            analyser.connect(ctx.destination);
            sourceRef.current = source;
            analyserRef.current = analyser;
            sourceNodeCache.set(audioEl, { source, analyser });
          }
          sourceElRef.current = audioEl;
        }
        return true;
      } catch (err) {
        // Non-fatal: the transport and readout both work from
        // <audio>'s own timeupdate events without this. Analysis is a
        // decoration layer only.
        Sentry.captureException(err, { tags: { area: "audio-level-analyser" } });
        return false;
      }
    };

    if (!setup() || !analyserRef.current) return;

    const analyser = analyserRef.current;
    const data = new Uint8Array(analyser.frequencyBinCount);

    const tick = (ts: number) => {
      if (cancelled) return;
      const dt = lastTsRef.current ? ts - lastTsRef.current : 16.7;
      lastTsRef.current = ts;

      analyser.getByteTimeDomainData(data);
      let sumSquares = 0;
      for (let i = 0; i < data.length; i++) {
        const v = (data[i] - 128) / 128;
        sumSquares += v * v;
      }
      const rms = Math.sqrt(sumSquares / data.length);
      const normalizedRms = normalizeRmsLevel(rms);

      const follower = followerRef.current;
      if (follower) {
        const followed = follower.push(normalizedRms, dt);
        if (!prefersReducedMotion) {
          setLevel(followed);
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    if (prefersReducedMotion) {
      // Reduced motion: paint once at the settled RMS envelope rather than
      // repainting every frame — the state is still carried, it just stops
      // animating (§03.1 motion note).
      analyser.getByteTimeDomainData(data);
      let sumSquares = 0;
      for (let i = 0; i < data.length; i++) {
        const v = (data[i] - 128) / 128;
        sumSquares += v * v;
      }
      setLevel(normalizeRmsLevel(Math.sqrt(sumSquares / data.length)));
    } else {
      rafRef.current = requestAnimationFrame(tick);
    }

    return () => {
      cancelled = true;
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [audioEl, isPlaying, prefersReducedMotion]);

  useEffect(() => {
    if (isPlaying) return;
    followerRef.current?.reset(0);
    // Deferred to a microtask so this reacts to the *external* isPlaying
    // transition rather than being a synchronous setState-during-effect
    // that could cascade with the tick loop's own frame-driven updates.
    queueMicrotask(() => setLevel(0));
  }, [isPlaying]);

  // Teardown the audio graph on unmount only.
  useEffect(() => {
    return () => {
      try {
        sourceRef.current?.disconnect();
        analyserRef.current?.disconnect();
        if (ctxRef.current && ctxRef.current.state !== "closed") {
          void ctxRef.current.close();
        }
      } catch {
        // best-effort teardown
      }
    };
  }, []);

  return level;
}
